import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, KeyRound, Loader2, ShieldCheck, UserCog, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { vide } from '../utils/listes';
import {
  fetchDroits,
  updateDroitsFonction,
  updateDroitsPersonne,
  updateDroitsStatut,
} from '../services/api';

type Droit = { code: string; libelle: string; aide: string };
type Famille = { code: string; libelle: string; droits: Droit[] };
type Statut = { code: string; libelle: string; aide: string; droits: string[] };
type Fonction = { id: number; code: string; label: string; niveau: number; droits: string[] };
type Personne = {
  id: number;
  nom: string;
  email: string;
  statut: string;
  administrateur: boolean;
  superAdministrateur: boolean;
  fonctionId: number | null;
  fonction: string | null;
  droits: string[];
};

const ONGLETS = [
  { code: 'statuts', libelle: 'Par statut', icone: Users },
  { code: 'fonctions', libelle: 'Par fonction', icone: KeyRound },
  { code: 'personnes', libelle: 'Par personne', icone: UserCog },
] as const;

type Onglet = (typeof ONGLETS)[number]['code'];

/**
 * Une grille de cases à cocher, réutilisée par les statuts et les fonctions.
 *
 * Les familles viennent du serveur : elles sont la même source que celle qui
 * tranche les appels. Une case qui existerait ici sans exister là-bas ferait
 * croire un droit accordé — c'est exactement ce qui s'est produit avec la
 * clôture d'exercice.
 */
function Grille({
  familles,
  coches,
  onBascule,
  desactive,
}: {
  familles: Famille[];
  coches: string[];
  onBascule: (code: string) => void;
  desactive?: boolean;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {familles.map((famille) => (
        <div key={famille.code} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            {famille.libelle}
          </p>
          <div className="space-y-1.5">
            {famille.droits.map((droit) => (
              <label
                key={droit.code}
                className={`flex items-start gap-2 text-sm ${desactive ? 'opacity-60' : 'cursor-pointer'}`}
                title={droit.aide}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={coches.includes(droit.code)}
                  disabled={desactive}
                  onChange={() => onBascule(droit.code)}
                />
                <span>
                  <span className="text-gray-900 dark:text-gray-100">{droit.libelle}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{droit.aide}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Les droits de l'espace, en un seul écran.
 *
 * Ils vivaient à trois endroits : la grille des fonctions dans un onglet des
 * référentiels, l'attribution des fonctions dans un autre écran, et le statut
 * d'administrateur nulle part — il se déduisait de la fonction. Donner à
 * quelqu'un une fonction autre que la présidence lui retirait donc son accès
 * d'administrateur, en silence. C'est ainsi qu'un président d'honneur à qui
 * l'on avait coché toute la grille se retrouvait devant des portes fermées :
 * la grille disait tout, et le statut venait de lui être retiré.
 *
 * Trois entrées ici, parce qu'il y a trois questions distinctes. Ce que donne
 * un statut — bénévole, membre du bureau, partenaire. Ce que donne une
 * fonction — président, trésorier, responsable des bénévoles. Et qui tient
 * quoi, personne par personne, avec le cumul affiché : c'est la réponse à
 * « pourquoi n'a-t-il pas accès à cela ».
 */
export default function GestionDroitsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [onglet, setOnglet] = useState<Onglet>('statuts');
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const etat = useQuery({ queryKey: ['droits-admin'], queryFn: fetchDroits });

  const rafraichir = (texte: string) => {
    setErreur(null);
    setMessage(texte);
    queryClient.invalidateQueries({ queryKey: ['droits-admin'] });
    setTimeout(() => setMessage(null), 2500);
  };
  const echouer = (e: unknown) => setErreur((e as Error).message);

  const majStatut = useMutation({
    mutationFn: ({ code, droits }: { code: string; droits: string[] }) => updateDroitsStatut(code, droits),
    onSuccess: () => rafraichir('Grille du statut enregistrée.'),
    onError: echouer,
  });
  const majFonction = useMutation({
    mutationFn: ({ id, droits }: { id: number; droits: string[] }) => updateDroitsFonction(id, droits),
    onSuccess: () => rafraichir('Grille de la fonction enregistrée.'),
    onError: echouer,
  });
  const majPersonne = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updateDroitsPersonne(id, payload),
    onSuccess: () => rafraichir('Modification enregistrée.'),
    onError: echouer,
  });

  const familles: Famille[] = etat.data?.familles ?? vide<Famille>();
  const statuts: Statut[] = etat.data?.statuts ?? [];
  const fonctions: Fonction[] = etat.data?.fonctions ?? [];
  const personnes: Personne[] = etat.data?.personnes ?? [];

  const parCode = useMemo(() => {
    const table: Record<string, string> = {};
    familles.forEach((f) => f.droits.forEach((d) => (table[d.code] = d.libelle)));
    return table;
  }, [familles]);

  const bascule = (liste: string[], code: string) =>
    liste.includes(code) ? liste.filter((c) => c !== code) : [...liste, code];

  if (etat.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Gestion des droits
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Ce que donne un statut, ce que donne une fonction, et qui tient quoi.
          </p>
        </div>
      </div>

      <div className="card">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Deux choses se cumulent, dans cet ordre. Le <strong>statut</strong> dit ce que voit une
          personne selon ce qu'elle est — bénévole, membre du bureau, partenaire. La{' '}
          <strong>fonction</strong>, quand elle en tient une, remplace ce réglage et décide seule.
        </p>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          L'<strong>administration du site</strong> est à part : elle ne se déduit d'aucune fonction
          et se donne personne par personne. Elle ouvre les paramètres, les sauvegardes, le journal
          et les données personnelles — ce qui n'apparaît dans aucune grille, parce qu'un membre du
          bureau pourrait sinon se promouvoir seul.
        </p>
      </div>

      {erreur && (
        <div className="card border-red-300 bg-red-50 dark:bg-red-950/30 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{erreur}</p>
        </div>
      )}
      {message && (
        <div className="card border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-700 dark:text-emerald-300">{message}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {ONGLETS.map(({ code, libelle, icone: Icone }) => (
          <button
            key={code}
            type="button"
            onClick={() => setOnglet(code)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium ${
              onglet === code
                ? 'bg-primary-700 text-white'
                : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            <Icone className="w-4 h-4" /> {libelle}
          </button>
        ))}
      </div>

      {onglet === 'statuts' && (
        <div className="space-y-4">
          {statuts.map((statut) => (
            <section key={statut.code} className="card space-y-3">
              <div>
                <h2 className="font-bold text-gray-900 dark:text-gray-100">{statut.libelle}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">{statut.aide}</p>
              </div>
              <Grille
                familles={familles}
                coches={statut.droits}
                onBascule={(code) =>
                  majStatut.mutate({ code: statut.code, droits: bascule(statut.droits, code) })
                }
                desactive={majStatut.isPending}
              />
            </section>
          ))}

          <section className="card">
            <h2 className="font-bold text-gray-900 dark:text-gray-100">Administrateur</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tous les droits, sans exception, plus l'administration du site. Ce statut n'a pas de
              grille : l'y soumettre permettrait de s'enfermer dehors, puisque c'est lui qui la
              tient. Il se donne dans l'onglet « Par personne ».
            </p>
          </section>
        </div>
      )}

      {onglet === 'fonctions' && (
        <div className="space-y-4">
          {fonctions.map((fonction) => (
            <section key={fonction.id} className="card space-y-3">
              <h2 className="font-bold text-gray-900 dark:text-gray-100">{fonction.label}</h2>
              <Grille
                familles={familles}
                coches={fonction.droits}
                onBascule={(code) =>
                  majFonction.mutate({ id: fonction.id, droits: bascule(fonction.droits, code) })
                }
                desactive={majFonction.isPending}
              />
            </section>
          ))}
        </div>
      )}

      {onglet === 'personnes' && (
        <section className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-3">Personne</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2 pr-3">Fonction</th>
                <th className="py-2 pr-3">Administre le site</th>
                <th className="py-2">Droits effectifs</th>
              </tr>
            </thead>
            <tbody>
              {personnes.map((personne) => (
                <tr key={personne.id} className="border-t border-gray-200 dark:border-gray-700 align-top">
                  <td className="py-2.5 pr-3">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {personne.nom || personne.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{personne.email}</p>
                  </td>

                  <td className="py-2.5 pr-3">
                    <select
                      className="input text-sm"
                      value={personne.statut === 'bureau' ? 'bureau' : 'benevole'}
                      disabled={majPersonne.isPending}
                      onChange={(e) =>
                        majPersonne.mutate({ id: personne.id, payload: { statut: e.target.value } })
                      }
                    >
                      <option value="benevole">Bénévole</option>
                      <option value="bureau">Membre du bureau</option>
                    </select>
                  </td>

                  <td className="py-2.5 pr-3">
                    <select
                      className="input text-sm"
                      value={personne.fonctionId ?? ''}
                      disabled={majPersonne.isPending || personne.statut !== 'bureau'}
                      onChange={(e) =>
                        majPersonne.mutate({
                          id: personne.id,
                          payload: { fonctionId: e.target.value === '' ? null : Number(e.target.value) },
                        })
                      }
                    >
                      <option value="">— aucune —</option>
                      {fonctions.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="py-2.5 pr-3">
                    {personne.superAdministrateur ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Support de la plateforme
                      </span>
                    ) : (
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={personne.administrateur}
                          disabled={majPersonne.isPending || personne.id === user?.id}
                          onChange={(e) =>
                            majPersonne.mutate({
                              id: personne.id,
                              payload: { administrateur: e.target.checked },
                            })
                          }
                        />
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {personne.id === user?.id ? 'vous-même' : 'administrateur'}
                        </span>
                      </label>
                    )}
                  </td>

                  {/* Le cumul, affiché : c'est ce qui manquait pour comprendre
                      pourquoi une personne se heurtait à une porte fermée. */}
                  <td className="py-2.5">
                    {personne.administrateur ? (
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Tous les droits
                      </span>
                    ) : personne.droits.length === 0 ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Aucun</span>
                    ) : (
                      <details>
                        <summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                          {personne.droits.length} droit{personne.droits.length > 1 ? 's' : ''}
                        </summary>
                        <ul className="mt-1 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          {personne.droits.map((code) => (
                            <li key={code}>{parCode[code] ?? code}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
